/**
 * Delivery-cockpit navigation policy (PSA v2 wedge).
 * Routes stay registered on develop builds; items are hidden from default sidebar/mobile nav.
 */

import { isDeliveryCockpitRouteEdition } from './psaRouteEdition';

/**
 * Ids hidden from daily sidebar nav for all roles.
 *
 * On public (main) builds, deferred URLs return a generic 404.
 * On develop builds, edition-limited roles see the edition guard on direct URL access.
 */
export const DELIVERY_COCKPIT_DEFERRED_NAV_IDS = new Set([
    'executive-dashboard',
    'overview-dashboard',
    'analytics-dashboard',
    'reports',
    'ai-intelligence',
    'observability',
    'helpdesk',
    'gps',
    'resource-planning',
    'pm-integration',
    'crm-integration',
    'communication',
    'document-control',
    'gantt-chart',
    'financial-management',
    'revenue-recognition',
    'quickbooks',
    'xero',
    'theme-showcase',
]);

/** Paths hidden from nav search unless role is explicitly allowed (develop builds). */
export const DELIVERY_COCKPIT_DEFERRED_SEARCH_PATHS = new Set([
    '/overview-dashboard',
    '/executive-dashboard',
    '/analytics-dashboard',
    '/reports',
    '/ai-intelligence',
    '/observability',
    '/helpdesk',
    '/gps-field-tracking',
    '/resource-planning',
    '/pm-integration',
    '/crm-integration',
    '/communication',
    '/document-control',
    '/gantt-chart',
    '/financial-management',
    '/revenue-recognition',
    '/quickbooks',
    '/xero',
    '/theme-showcase',
]);

/** Wedge home for staffing — replaces standalone /resource-planning. */
export const PROJECT_OPS_STAFFING_PATH = '/project-operations?tab=staffing';

/**
 * Filters navigation items for delivery-cockpit IA.
 *
 * @param {Array<{ id: string }>} items - Nav items from EnhancedNavigation.
 * @returns {Array<{ id: string }>} Items visible in the sidebar.
 */
export const filterDeliveryCockpitNavItems = (items) => (
    items.filter((item) => !DELIVERY_COCKPIT_DEFERRED_NAV_IDS.has(item.id))
);

/**
 * Filters global search results to match delivery-cockpit nav visibility.
 *
 * @param {Array<{ path: string }>} items - Search result entries.
 * @param {string|null|undefined} userRole - Auth user role.
 * @returns {Array<{ path: string }>} Items visible in search for the role.
 */
export const filterDeliveryCockpitSearchData = (items, userRole) => (
    items.filter((item) => {
        const path = item?.path?.split('?')[0];
        if (!DELIVERY_COCKPIT_DEFERRED_SEARCH_PATHS.has(path)) {
            return true;
        }
        if (!isDeliveryCockpitRouteEdition()) {
            return false;
        }
        if (userRole === 'admin') {
            return true;
        }
        if (path === '/executive-dashboard' || path === '/overview-dashboard') {
            return EXECUTIVE_DASHBOARD_ACCESS_ROLES.has(userRole);
        }
        return false;
    })
);

/** Roles that may use the executive dashboard (strategic portfolio KPI wall). */
export const EXECUTIVE_DASHBOARD_ROLES = new Set([
    'admin',
    'director',
    'ceo',
    'cto',
    'cfo',
]);

/** Director-tier roles — deferred from delivery cockpit wedge nav (develop builds). */
export const EXECUTIVE_DASHBOARD_ACCESS_ROLES = new Set([
    'director',
    'ceo',
    'cto',
    'cfo',
]);

/** Wedge personas blocked from enterprise-deferred modules on develop builds. */
export const DELIVERY_COCKPIT_EDITION_LIMITED_ROLES = new Set([
    'project_manager',
    'employee',
    'team_leader',
    'manager',
    'finance',
    'hr_manager',
    'client',
]);

/**
 * True when the role should see the Delivery Cockpit edition guard on deferred routes.
 *
 * @param {string|null|undefined} role - Auth user role.
 * @returns {boolean}
 */
export const isDeliveryCockpitEditionLimitedRole = (role) => {
    if (!isDeliveryCockpitRouteEdition()) {
        return true;
    }
    if (!role || typeof role !== 'string') {
        return true;
    }
    if (EXECUTIVE_DASHBOARD_ROLES.has(role)) {
        return false;
    }
    return DELIVERY_COCKPIT_EDITION_LIMITED_ROLES.has(role);
};

/**
 * Delivery-cockpit roles that should not land on executive KPI dashboards.
 *
 * @param {string|null|undefined} role - Auth user role.
 * @returns {boolean} True when role should use project-operations instead of /executive-dashboard.
 */
export const shouldRedirectFromExecutiveDashboard = (role) => {
    if (!isDeliveryCockpitRouteEdition()) {
        return true;
    }
    if (!role || typeof role !== 'string') {
        return true;
    }
    if (role === 'admin') {
        return true;
    }
    if (EXECUTIVE_DASHBOARD_ACCESS_ROLES.has(role)) {
        return false;
    }
    return true;
};
